import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/Calendar';
import { localDateToIso } from '@/utils/dateRangePicker';
import type {
  FlipkartCampaignEndDateJob,
  FlipkartCampaignEndDateResult,
} from '@/api/flipkartCampaignEndDateJob';

export function FlipkartCampaignEndDateModal({
  selectionCount,
  pending,
  job = null,
  failedResults = [],
  error = null,
  onCancel,
  onConfirm,
  onRetryFailed,
}: {
  selectionCount: number;
  pending: boolean;
  job?: FlipkartCampaignEndDateJob | null;
  failedResults?: FlipkartCampaignEndDateResult[];
  error?: string | null;
  onCancel: () => void;
  onConfirm: (endDate: string | null) => void;
  onRetryFailed?: () => void;
}) {
  const [draft, setDraft] = useState<Date | undefined>();
  const noun = selectionCount === 1 ? 'campaign' : 'campaigns';

  if (job) {
    return (
      <FlipkartCampaignEndDateProgress
        job={job}
        failedResults={failedResults}
        isRunning={pending}
        onClose={onCancel}
        onRetryFailed={onRetryFailed}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex w-full max-w-md flex-col rounded-xl bg-surface p-5 shadow-xl">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          onClick={onCancel}
          disabled={pending}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="pr-8 text-base font-semibold text-foreground">
          Change end date for {selectionCount} {noun}
        </h3>
        <p className="mt-2 text-body-sm text-foreground-muted">
          The selected date replaces the end date on every selected campaign.
        </p>
        <div className="mt-4 flex justify-center">
          <Calendar
            mode="single"
            selected={draft}
            onSelect={setDraft}
            defaultMonth={draft}
            aria-label="End date"
          />
        </div>
        {error ? <p className="mt-3 text-body-sm text-error">{error}</p> : null}
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            className="mr-auto"
            onClick={() => onConfirm(null)}
            disabled={pending}
          >
            Clear
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (!draft) return;
              onConfirm(localDateToIso(draft));
            }}
            disabled={pending || !draft}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FlipkartCampaignEndDateProgress({
  job,
  failedResults,
  isRunning,
  onClose,
  onRetryFailed,
}: {
  job: FlipkartCampaignEndDateJob;
  failedResults: FlipkartCampaignEndDateResult[];
  isRunning: boolean;
  onClose: () => void;
  onRetryFailed?: () => void;
}) {
  const total = job.applyProgress.total ?? job.totals.total ?? 0;
  const processed = job.applyProgress.processed ?? 0;
  const pct =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : isRunning ? 0 : 100;
  const failed = job.totals.failed ?? 0;
  const applied = job.totals.applied ?? 0;
  const skipped = job.totals.skipped ?? 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">
          {isRunning ? 'Working on it' : 'End date results'}
        </h3>
        <p className="mt-1 text-sm text-foreground-muted">
          {isRunning
            ? `Updating campaigns… ${processed} of ${total || '—'}`
            : job.status === 'failed'
              ? job.applyProgress.error || 'End date job failed'
              : `Applied ${applied}, skipped ${skipped}, failed ${failed}`}
        </p>
        <div className="mt-4">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-border"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="End date update progress"
          >
            <div
              className="h-full rounded-full bg-foreground transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-foreground-muted">{pct}%</p>
        </div>
        {!isRunning && failed > 0 ? (
          <div className="app-table-scroll mt-4 max-h-56 min-w-0 max-w-full overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-surface-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Campaign</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {failedResults.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 text-foreground-muted" colSpan={2}>
                      {failed} campaign{failed === 1 ? '' : 's'} failed.
                    </td>
                  </tr>
                ) : (
                  failedResults.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 text-foreground">
                        {row.campaignName || row.campaignId}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {row.errorMessage || 'Failed'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          {!isRunning && failed > 0 ? (
            <Button type="button" onClick={onRetryFailed} disabled={isRunning}>
              Retry failed
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose} disabled={isRunning}>
            {isRunning ? 'Running…' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
